/**
 * Transactions data hooks (FE-2, PHASE1 §7.3 / §4.1 / §4.3).
 *
 * - `useTransactionsList` — offset/limit infinite query keyed by the normalised
 *   filter object (`qk.transactionsList`). `getNextPageParam` uses `meta.total`
 *   from the last page; the full-set `total` and `sum_amount` come from the same
 *   meta and are surfaced for the list footer.
 * - `useTransaction` — single detail query (`qk.transactionDetail`).
 * - `useTransactionFilters` — filters ↔ URL round-trip via `useSearchParams`, so
 *   a shared, deterministic filter object drives both the query key and the CSV
 *   export query string (`filtersToSearchParams`).
 * - inline mutations: re-categorize / skip / reopen. Each invalidates
 *   `['transactions']` + `['inbox']` (§7.3).
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '../api.ts';
import { qk } from '../queryKeys.ts';
import type {
  CategorizeIn,
  CategorizeSource,
  Money,
  ReviewStatus,
  SplitInput,
  TransactionDetailOut,
  TransactionFilters,
  TransactionListMeta,
  TransactionOut,
  TransactionSort,
  TransactionStatus,
  TransactionUpdate,
} from '../types.ts';

/** Fixed page size for the transactions list (§4.2, `limit` default 50). */
export const PAGE_SIZE = 50;

const STALE = 30 * 1000;

const ALLOWED_SORTS: readonly TransactionSort[] = [
  'booking_date',
  '-booking_date',
  'amount',
  '-amount',
  'created_at',
  '-created_at',
];
const REVIEW_STATUSES: readonly ReviewStatus[] = ['pending', 'reviewed', 'skipped'];
const STATUSES: readonly TransactionStatus[] = ['booked', 'pending'];

// ── Filter ↔ query-string helpers (shared by list fetch + CSV export) ────────

/**
 * Serialise a filter object into `URLSearchParams` — the single source of the
 * filter query string used by both `GET /api/transactions` and `export.csv`.
 * Array filters repeat their key (`account_id=1&account_id=2`); `limit`/`offset`
 * are intentionally NOT included here (the list hook appends them).
 */
export function filtersToSearchParams(filters: TransactionFilters): URLSearchParams {
  const params = new URLSearchParams();
  const appendAll = (key: string, values?: readonly (string | number)[]) => {
    for (const value of values ?? []) params.append(key, String(value));
  };
  appendAll('account_id', filters.account_id);
  appendAll('bucket_id', filters.bucket_id);
  if (filters.contract_id !== undefined) params.set('contract_id', String(filters.contract_id));
  appendAll('review_status', filters.review_status);
  appendAll('status', filters.status);
  appendAll('tag', filters.tag);
  if (filters.date_from) params.set('date_from', filters.date_from);
  if (filters.date_to) params.set('date_to', filters.date_to);
  if (filters.q) params.set('q', filters.q);
  if (filters.amount_min) params.set('amount_min', filters.amount_min);
  if (filters.amount_max) params.set('amount_max', filters.amount_max);
  if (filters.include_deleted) params.set('include_deleted', 'true');
  if (filters.sort) params.set('sort', filters.sort);
  return params;
}

function parseNumbers(values: string[]): number[] {
  return values
    .map((v) => Number(v))
    .filter((n) => Number.isFinite(n))
    .sort((a, b) => a - b);
}

/**
 * Parse `URLSearchParams` into a canonical `TransactionFilters` object: only
 * present keys, numeric arrays sorted, enums validated. Deterministic so a
 * URL → filters → URL round-trip yields a stable query key.
 */
export function parseTransactionFilters(params: URLSearchParams): TransactionFilters {
  const out: TransactionFilters = {};

  const accounts = parseNumbers(params.getAll('account_id'));
  if (accounts.length) out.account_id = accounts;
  const buckets = parseNumbers(params.getAll('bucket_id'));
  if (buckets.length) out.bucket_id = buckets;

  const contractId = params.get('contract_id');
  if (contractId !== null) {
    const parsed = Number.parseInt(contractId, 10);
    if (Number.isFinite(parsed)) out.contract_id = parsed;
  }

  const review = params
    .getAll('review_status')
    .filter((v): v is ReviewStatus => (REVIEW_STATUSES as readonly string[]).includes(v));
  if (review.length) out.review_status = review;

  const status = params
    .getAll('status')
    .filter((v): v is TransactionStatus => (STATUSES as readonly string[]).includes(v));
  if (status.length) out.status = status;

  const tags = params.getAll('tag').filter((t) => t.length > 0);
  if (tags.length) out.tag = tags;

  const dateFrom = params.get('date_from');
  if (dateFrom) out.date_from = dateFrom;
  const dateTo = params.get('date_to');
  if (dateTo) out.date_to = dateTo;
  const q = params.get('q');
  if (q) out.q = q;
  const amountMin = params.get('amount_min');
  if (amountMin) out.amount_min = amountMin;
  const amountMax = params.get('amount_max');
  if (amountMax) out.amount_max = amountMax;
  if (params.get('include_deleted') === 'true') out.include_deleted = true;

  const sort = params.get('sort');
  if (sort && (ALLOWED_SORTS as readonly string[]).includes(sort)) {
    out.sort = sort as TransactionSort;
  }

  return out;
}

export interface TransactionFiltersController {
  filters: TransactionFilters;
  setFilters: (next: TransactionFilters) => void;
  patch: (partial: Partial<TransactionFilters>) => void;
  reset: () => void;
}

/** Filters synced to the URL query string (round-trip). */
export function useTransactionFilters(): TransactionFiltersController {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseTransactionFilters(searchParams), [searchParams]);

  const setFilters = useCallback(
    (next: TransactionFilters) => {
      setSearchParams(filtersToSearchParams(next), { replace: true });
    },
    [setSearchParams],
  );

  const patch = useCallback(
    (partial: Partial<TransactionFilters>) => {
      setSearchParams(filtersToSearchParams({ ...filters, ...partial }), { replace: true });
    },
    [filters, setSearchParams],
  );

  const reset = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  return { filters, setFilters, patch, reset };
}

// ── List (infinite, offset/limit) ───────────────────────────────────────────

interface TransactionPage {
  items: TransactionOut[];
  meta?: TransactionListMeta;
}

export interface TransactionsListResult {
  items: TransactionOut[];
  /** Full filtered-set count (not the loaded page) from `meta.total`. */
  total: number;
  /** Signed sum over the full filtered set from `meta.sum_amount`. */
  sumAmount: Money;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  refetch: () => void;
}

/** Offset/limit infinite query over `/api/transactions` (§4.1/§4.2). */
export function useTransactionsList(filters: TransactionFilters): TransactionsListResult {
  const query = useInfiniteQuery({
    queryKey: qk.transactionsList(filters),
    queryFn: async ({ pageParam }) => {
      const params = filtersToSearchParams(filters);
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(pageParam));
      const envelope = await api.getEnvelope<TransactionOut[], TransactionListMeta>(
        `/api/transactions?${params.toString()}`,
      );
      return { items: envelope.data, meta: envelope.meta } satisfies TransactionPage;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, page) => sum + page.items.length, 0);
      const total = lastPage.meta?.total ?? loaded;
      return loaded < total ? loaded : undefined;
    },
    staleTime: STALE,
  });

  // Flatten + dedupe by id: with offset pagination, rows inserted at the top of
  // the sort order between page fetches (EB cron sync, the partner's actions)
  // shift the offsets, so the next page can re-serve rows already loaded —
  // which would render duplicate rows with duplicate React keys. Note
  // `getNextPageParam` above must keep counting RAW per-page items (server
  // offsets), never this deduped length.
  const items = useMemo(() => {
    const seen = new Set<number>();
    const out: TransactionOut[] = [];
    for (const page of query.data?.pages ?? []) {
      for (const txn of page.items) {
        if (seen.has(txn.id)) continue;
        seen.add(txn.id);
        out.push(txn);
      }
    }
    return out;
  }, [query.data]);
  const pages = query.data?.pages;
  const lastMeta = pages && pages.length > 0 ? pages[pages.length - 1].meta : undefined;

  return {
    items,
    total: lastMeta?.total ?? items.length,
    sumAmount: lastMeta?.sum_amount ?? '0.00',
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: () => {
      void query.fetchNextPage();
    },
    refetch: () => {
      void query.refetch();
    },
  };
}

// ── Detail ───────────────────────────────────────────────────────────────────

/** Single transaction detail (splits + provenance). Skips when `id` is null. */
export function useTransaction(id: number | null): UseQueryResult<TransactionDetailOut> {
  return useQuery({
    queryKey: id === null ? ['transactions', 'detail', 'disabled'] : qk.transactionDetail(id),
    queryFn: () => api.get<TransactionDetailOut>(`/api/transactions/${id}`),
    enabled: id !== null,
  });
}

// ── Inline mutations (re-categorize / skip / reopen) ─────────────────────────

/** Invalidate every view that a transaction change can affect (§7.3). */
function useInvalidateTransactionViews(): () => void {
  const qc = useQueryClient();
  return useCallback(() => {
    void qc.invalidateQueries({ queryKey: qk.transactions });
    void qc.invalidateQueries({ queryKey: qk.inbox });
    void qc.invalidateQueries({ queryKey: qk.reports });
    void qc.invalidateQueries({ queryKey: qk.budgets });
  }, [qc]);
}

export interface RecategorizeVars {
  id: number;
  splits: SplitInput[];
  /** `grid`/`split` → user; `accept` → suggestion_accepted (§4.3). */
  source?: CategorizeSource;
}

/** POST `/categorize` — replaces splits, sets review_status=reviewed. */
export function useRecategorizeTransaction() {
  const invalidate = useInvalidateTransactionViews();
  return useMutation({
    mutationFn: ({ id, splits, source = 'grid' }: RecategorizeVars) =>
      api.post<TransactionDetailOut>(`/api/transactions/${id}/categorize`, {
        splits,
        source,
      } satisfies CategorizeIn),
    onSuccess: invalidate,
  });
}

/** POST `/skip` — review_status=skipped. */
export function useSkipTransaction() {
  const invalidate = useInvalidateTransactionViews();
  return useMutation({
    mutationFn: (id: number) => api.post<TransactionDetailOut>(`/api/transactions/${id}/skip`),
    onSuccess: invalidate,
  });
}

/** POST `/reopen` — review_status=pending, clears splits (undo). */
export function useReopenTransaction() {
  const invalidate = useInvalidateTransactionViews();
  return useMutation({
    mutationFn: (id: number) => api.post<TransactionDetailOut>(`/api/transactions/${id}/reopen`),
    onSuccess: invalidate,
  });
}

export interface UpdateTransactionVars {
  id: number;
  body: TransactionUpdate;
}

/** PATCH — partial edit (notes, tags, payee, …; §4.3). `notes: null` clears. */
export function useUpdateTransaction() {
  const invalidate = useInvalidateTransactionViews();
  return useMutation({
    mutationFn: ({ id, body }: UpdateTransactionVars) =>
      api.patch<TransactionDetailOut>(`/api/transactions/${id}`, body),
    onSuccess: invalidate,
  });
}

/** Count of non-empty filter groups — drives the "Filter" badge. */
export function countActiveFilters(filters: TransactionFilters): number {
  let count = 0;
  if (filters.account_id?.length) count += 1;
  if (filters.bucket_id?.length) count += 1;
  if (filters.contract_id !== undefined) count += 1;
  if (filters.review_status?.length) count += 1;
  if (filters.status?.length) count += 1;
  if (filters.tag?.length) count += 1;
  if (filters.date_from) count += 1;
  if (filters.date_to) count += 1;
  if (filters.q) count += 1;
  if (filters.amount_min) count += 1;
  if (filters.amount_max) count += 1;
  if (filters.include_deleted) count += 1;
  return count;
}
